// src/analytics/MixpanelProvider.tsx
import {ConsoleLogger} from 'aws-amplify/utils';
import React, {useEffect, useMemo} from 'react';

import {MixpanelCtx} from './MixpanelCtx';
import {FallbackEmitter} from './fallbackEmitter';
import type {MixPanelService} from './mixpanel.service';
import {VITE_API_ENDPOINT, VITE_MIXPANEL_TOKEN} from '../config';

const logger = new ConsoleLogger('MixpanelProvider');

type AppelDiffere = {methode: string; args: unknown[]};

/** Ce que la façade délègue : le service complet, ou l'émetteur de secours. */
type ClientMixpanel = Pick<MixPanelService, 'track' | 'identifyUser' | 'logout' | 'setPageContext'>;

type EtatFacade = {
  reel?: ClientMixpanel;
  /** Vrai quand même l'émetteur de secours n'a pas pu être chargé : on cesse alors d'empiler. */
  abandonne: boolean;
  journal: AppelDiffere[];
};

/**
 * Façade d'identité stable placée dans le contexte.
 *
 * `mixpanel-browser` n'est plus importé statiquement : un bloqueur de traqueurs
 * refuse son URL de développement (`/node_modules/.vite/deps/mixpanel-browser.js`
 * correspond aux listes de filtres), et un import statique faisait alors échouer
 * le module d'entrée — l'application entière restait blanche, sans erreur ni
 * repli, puisque la requête est coupée avant d'atteindre Vite.
 *
 * Toutes les méthodes publiques du service renvoient void et aucun appelant n'en
 * consomme le retour : un proxy suffit. Les appels émis avant la fin du
 * chargement sont rejoués dans l'ordre, pour ne pas perdre l'amorce de session
 * ni désordonner l'identification.
 */
function creerFacade(etat: EtatFacade): MixPanelService {
  return new Proxy({} as MixPanelService, {
    get(_cible, methode) {
      return (...args: unknown[]) => {
        if (etat.reel !== undefined) {
          (etat.reel[methode as keyof ClientMixpanel] as (...a: unknown[]) => void)(...args);
          return;
        }
        if (!etat.abandonne) {
          etat.journal.push({methode: String(methode), args});
        }
      };
    },
  });
}

export function MixpanelProvider({children}: {children: React.ReactNode}) {
  const etat = useMemo<EtatFacade>(() => ({abandonne: false, journal: []}), []);
  const service = useMemo(() => creerFacade(etat), [etat]);

  useEffect(() => {
    let annule = false;
    const brancher = (client: ClientMixpanel) => {
      etat.reel = client;
      for (const {methode, args} of etat.journal.splice(0)) {
        (client[methode as keyof ClientMixpanel] as (...a: unknown[]) => void)(...args);
      }
    };
    void import('./mixpanel.service')
      .then(({MixPanelService}) => {
        if (annule) return;
        brancher(new MixPanelService(VITE_MIXPANEL_TOKEN));
      })
      .catch((error) => {
        // Bloqueur de publicité : le module `mixpanel-browser` est refusé. Les événements partent
        // alors par l'émetteur de secours (importé statiquement, donc rien à refuser), via le
        // relais de l'API, au lieu d'être jetés.
        if (annule) return;
        logger.warn('`mixpanel-browser` indisponible : émetteur de secours via l’API.', error);
        try {
          brancher(new FallbackEmitter(VITE_MIXPANEL_TOKEN, VITE_API_ENDPOINT));
        } catch (fallbackError) {
          etat.abandonne = true;
          etat.journal.splice(0);
          logger.warn('Télémétrie désactivée : aucun émetteur n’a pu démarrer.', fallbackError);
        }
      });
    return () => {
      annule = true;
    };
  }, [etat]);

  return <MixpanelCtx.Provider value={service}>{children}</MixpanelCtx.Provider>;
}
